import type {
  IResponseConversation,
  IResponseConversationDraftMessage,
  IResponseConversationMentionUnread,
  IResponseConversationUnread,
  IResponseEventMessageDeletedDraft,
} from 'entities/Conversation/api/types'
import type {
  IResponseIntegrationDisconnected,
  IResponseIntegrationListSyncFinished,
  IResponseIntegrationRefreshed,
  IResponseUserIntegration,
} from 'entities/Integrations/api/types'
import {
  type IResponseEventOrganizationCalling,
  type IResponseEventUpdateBalance,
  type IResponseOrganizationRestrictionRemoved,
} from 'entities/Organization/api/types'
import type {
  IResponseCallQueueParticipant,
  IResponseCallTransferFailed,
  IResponseCallTransferStarted,
  IResponseEventConferenceParticipant,
  IResponseEventConversationBulkFavorite,
  IResponseEventConversationCalling,
  IResponseEventCalling,
} from 'entities/Call/api/types'
import type { IResponseEventMessage, IResponseEventMessagePopper } from 'entities/Message/api/types'
import {
  type IResponseEventInboxCalling,
  type IResponseEventInboxMemberAdded,
  type IResponseEventInboxMemberRemoved,
  type IResponseInboxBulkUserAttached,
  type IResponseInboxBulkUserRemoved,
  type IResponseInboxCombine,
  type IResponseInboxDeleted,
} from 'entities/Inbox/api/types'
import type {
  IResponseAttachmentUpload,
  IResponseEventAttachmentUploadError,
  IResponseMedia,
  IResponseUploadSuccess,
} from 'entities/Attachment/api/types'
import type { IResponseEventTyping } from 'entities/Typing/api/types'
import type { IResponseEventActivityCreated } from 'entities/Activity/api/types'
import type { IResponseContact } from 'entities/Contacts/api/types'
import type { IResponseEventBroadcast } from 'entities/Broadcast/api/types'
import type { IResponseEventViewing } from 'entities/Viewing'
import type { IResponseKnowledgeBase, IResponseKnowledgeBaseDocument } from 'entities/KnowledgeBase'
import type {
  IResponseEventRegisterOrganizationError,
  IResponseEventTokenCreated,
} from 'entities/Auth'
import {
  type IResponseEventCallerIdVerificationResult,
  type IResponseEventCreateBulkInboxPerNumber,
} from 'entities/Phone/api/types'
import type { IResponsePowerDialer } from 'entities/PowerDialer/api/types'
import {
  type IResponseEventAwayStatusChanged,
  type IResponseEventInviteRevoke,
  type IResponseEventInviteResent,
  type IResponseEventToggleRecordAutomatically,
  type IResponseEventToggleRingEverywhere,
  type IResponseEventAccessRequestRevoked,
} from 'entities/Users/api/types'
import { type IResponseEventChatbotAppointmentBooked } from 'entities/Chatbot'
import { type IResponseEventOrganizationHubspotWorkflowSyncFinished } from 'entities/HubspotWorkflows/api/types'
import { type IResponseEventUserPermissions } from 'entities/UsersPermissions/api/types'

type WasteChar = '-' | '.' | '_'
type NormalChar = '.'

type NormalProp<Prop extends string> = Prop extends `${infer Char}${infer Rest}`
  ? `${Char extends WasteChar ? NormalChar : Char}${NormalProp<Rest>}`
  : Prop

type NormalName<Name extends string> = Name extends `${infer Part}${NormalChar}${infer Rest}`
  ? Capitalize<`${Part}${Capitalize<NormalName<Rest>>}`>
  : Name

type WebsocketEventName<Name extends string> = NormalName<NormalProp<Name>>

export type IWebsocketAction = {
  'conversation.contact.opted-out': IResponseConversation
  'conversation.contact.opted-in': IResponseConversation
  'conversation.reassigned': IResponseConversation
  'conversation.closed': IResponseConversation
  'conversation.opened': IResponseConversation
  'conversation.unread': IResponseConversationUnread
  'conversation.mention.unread': IResponseConversationMentionUnread
  'conversation.state-changed': IResponseConversation
  'inbox.calling': IResponseEventInboxCalling
  'inbox.created': IResponseInboxCombine
  'inbox.updated': IResponseInboxCombine
  'inbox.deleted': IResponseInboxDeleted
  'inbox.bulk.user.attached': IResponseInboxBulkUserAttached
  'inbox.bulk.user.removed': IResponseInboxBulkUserRemoved
  'integration.connected': IResponseUserIntegration
  'integration.disconnected': IResponseIntegrationDisconnected
  'integration.users.refreshed': IResponseIntegrationRefreshed
  'integration.list.sync.finished': IResponseIntegrationListSyncFinished
  'message.popper': IResponseEventMessagePopper
  'organization.calling': IResponseEventOrganizationCalling
  'account_balance.updated': IResponseEventUpdateBalance
  'conference.participant_join': IResponseEventConferenceParticipant
  'conference.participant_leave': IResponseEventConferenceParticipant
  'conversations.bulk.favorite': IResponseEventConversationBulkFavorite
  'message.received.new': IResponseEventMessage
  'message.updated.new': IResponseEventMessage
  'message.transcript.started': IResponseEventMessage
  'message.transcript.completed': IResponseEventMessage
  'message.transcript.failed': IResponseEventMessage
  'voicemail.received': IResponseEventMessage
  'conversations.activity.created': IResponseEventActivityCreated
  'client-typing': IResponseEventTyping
  'client-viewing': IResponseEventViewing
  'client-calling': IResponseEventCalling
  'client-conversation-calling': IResponseEventConversationCalling
  'attachment.video.converted': IResponseAttachmentUpload
  'contact.updated': IResponseContact
  'client-draft': IResponseConversationDraftMessage
  'message.deleted.draft': IResponseEventMessageDeletedDraft
  'call.queue_participant_join': IResponseCallQueueParticipant
  'call.queue_participant_leave': IResponseCallQueueParticipant
  'call.call_transfer_started': IResponseCallTransferStarted
  'call.call_transfer_failed': IResponseCallTransferFailed
  'attachment.upload.error': IResponseEventAttachmentUploadError
  'attachment.upload.successful': IResponseUploadSuccess
  'attachment.thumbnail.created': IResponseMedia
  'broadcast.canceled': IResponseEventBroadcast
  'knowledge_base.updated': IResponseKnowledgeBase
  'knowledge_base_document.updated': IResponseKnowledgeBaseDocument
  'token.created': IResponseEventTokenCreated
  'register.organization.error': IResponseEventRegisterOrganizationError
  'number.caller_id_verification_result': IResponseEventCallerIdVerificationResult
  'pd-campaigns.created': IResponsePowerDialer
  'pd-campaigns.updated': IResponsePowerDialer
  'user.away_status_changed': IResponseEventAwayStatusChanged
  'chatbot.appointment-booked.test-mode': IResponseEventChatbotAppointmentBooked
  'settings.numbers.unassigned.refresh.finished': []
  'inbox.bulk_creation_finished': IResponseEventCreateBulkInboxPerNumber
  'organization_hubspot_workflow.sync.finished': IResponseEventOrganizationHubspotWorkflowSyncFinished
  'integration.numbers.refreshed': IResponseIntegrationRefreshed
  'organization.restriction.removed': IResponseOrganizationRestrictionRemoved
  'permissions.updated': IResponseEventUserPermissions
  'inbox.member.removed': IResponseEventInboxMemberRemoved
  'inbox.member.added': IResponseEventInboxMemberAdded
  'invitation.revoked': IResponseEventInviteRevoke
  'invitation.resent': IResponseEventInviteResent
  'user.toggle_record_automatically': IResponseEventToggleRecordAutomatically
  'user.toggle_ring_everywhere': IResponseEventToggleRingEverywhere
  'conversation.shortcode.opt-out.changed': IResponseContact
  'access-request.revoked': IResponseEventAccessRequestRevoked
}

export const WebsocketEvent: { [Key in keyof IWebsocketAction as WebsocketEventName<Key>]: Key } = {
  ConversationContactOptedOut: 'conversation.contact.opted-out',
  ConversationContactOptedIn: 'conversation.contact.opted-in',
  ConversationReassigned: 'conversation.reassigned',
  ConversationClosed: 'conversation.closed',
  ConversationOpened: 'conversation.opened',
  ConversationUnread: 'conversation.unread',
  ConversationMentionUnread: 'conversation.mention.unread',
  ConversationStateChanged: 'conversation.state-changed',
  InboxCalling: 'inbox.calling',
  IntegrationConnected: 'integration.connected',
  IntegrationDisconnected: 'integration.disconnected',
  IntegrationUsersRefreshed: 'integration.users.refreshed',
  IntegrationListSyncFinished: 'integration.list.sync.finished',
  MessagePopper: 'message.popper',
  OrganizationCalling: 'organization.calling',
  AccountBalanceUpdated: 'account_balance.updated',
  ConferenceParticipantJoin: 'conference.participant_join',
  ConferenceParticipantLeave: 'conference.participant_leave',
  ConversationsBulkFavorite: 'conversations.bulk.favorite',
  MessageReceivedNew: 'message.received.new',
  MessageUpdatedNew: 'message.updated.new',
  VoicemailReceived: 'voicemail.received',
  ConversationsActivityCreated: 'conversations.activity.created',
  ClientTyping: 'client-typing',
  ClientViewing: 'client-viewing',
  AttachmentVideoConverted: 'attachment.video.converted',
  ContactUpdated: 'contact.updated',
  ClientDraft: 'client-draft',
  MessageDeletedDraft: 'message.deleted.draft',
  CallQueueParticipantJoin: 'call.queue_participant_join',
  CallQueueParticipantLeave: 'call.queue_participant_leave',
  CallCallTransferStarted: 'call.call_transfer_started',
  CallCallTransferFailed: 'call.call_transfer_failed',
  MessageTranscriptStarted: 'message.transcript.started',
  MessageTranscriptCompleted: 'message.transcript.completed',
  MessageTranscriptFailed: 'message.transcript.failed',
  InboxUpdated: 'inbox.updated',
  InboxDeleted: 'inbox.deleted',
  InboxCreated: 'inbox.created',
  InboxBulkUserAttached: 'inbox.bulk.user.attached',
  InboxBulkUserRemoved: 'inbox.bulk.user.removed',
  AttachmentUploadError: 'attachment.upload.error',
  AttachmentUploadSuccessful: 'attachment.upload.successful',
  AttachmentThumbnailCreated: 'attachment.thumbnail.created',
  BroadcastCanceled: 'broadcast.canceled',
  KnowledgeBaseUpdated: 'knowledge_base.updated',
  KnowledgeBaseDocumentUpdated: 'knowledge_base_document.updated',
  TokenCreated: 'token.created',
  RegisterOrganizationError: 'register.organization.error',
  ClientCalling: 'client-calling',
  ClientConversationCalling: 'client-conversation-calling',
  NumberCallerIdVerificationResult: 'number.caller_id_verification_result',
  PdCampaignsCreated: 'pd-campaigns.created',
  PdCampaignsUpdated: 'pd-campaigns.updated',
  UserAwayStatusChanged: 'user.away_status_changed',
  ChatbotAppointmentBookedTestMode: 'chatbot.appointment-booked.test-mode',
  SettingsNumbersUnassignedRefreshFinished: 'settings.numbers.unassigned.refresh.finished',
  InboxBulkCreationFinished: 'inbox.bulk_creation_finished',
  OrganizationHubspotWorkflowSyncFinished: 'organization_hubspot_workflow.sync.finished',
  IntegrationNumbersRefreshed: 'integration.numbers.refreshed',
  OrganizationRestrictionRemoved: 'organization.restriction.removed',
  PermissionsUpdated: 'permissions.updated',
  InboxMemberRemoved: 'inbox.member.removed',
  InboxMemberAdded: 'inbox.member.added',
  InvitationRevoked: 'invitation.revoked',
  InvitationResent: 'invitation.resent',
  UserToggleRecordAutomatically: 'user.toggle_record_automatically',
  UserToggleRingEverywhere: 'user.toggle_ring_everywhere',
  ConversationShortcodeOptOutChanged: 'conversation.shortcode.opt-out.changed',
  AccessRequestRevoked: 'access-request.revoked',
}
